import { useParams } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { DetailError } from "@/components/DetailError";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { VenueGuideBody } from "@/components/VenueGuideBody";
import { CTAButton } from "@/components/CTAButton";
import { EventQuickPlan } from "@/components/EventQuickPlan";
import { ShareBar } from "@/components/ShareBar";
import { TicketAvailability } from "@/components/TicketAvailability";
import { onImgError } from "@/lib/images";
import { formatEventDate } from "@/lib/dates";
import { Calendar, MapPin } from "lucide-react";

export default function EventPage() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/event-guide/${slug}`, [slug]);

  if (loading) return <Loading />;
  if (error || !data?.event) return <DetailError error={error} path={`/events/${slug}`} notFound={!error} />;
  const e = data.event;
  const v = data.venue;
  const dateLabel = formatEventDate(e.date, e.timezone, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const jsonLd = {
    "@context": "https://schema.org", "@type": "SportsEvent", name: e.title,
    startDate: e.date, eventStatus: "https://schema.org/EventScheduled",
    location: v ? { "@type": "Place", name: v.name, address: v.address } : undefined,
  };

  return (
    <>
      <Seo title={e.seo_title || `${e.title} — Where to Stay | Hotelstrips`} description={e.seo_description || e.description}
        image={e.image_url} path={`/events/${e.slug}`} type="article" jsonLd={jsonLd} />

      <section className="relative min-h-[56vh] flex items-end">
        <img src={e.image_url} alt={e.image_alt || e.title} onError={onImgError} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 img-overlay" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full pb-12">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="rounded-full bg-primary px-3 py-1 text-xs font-heading font-bold text-primary-foreground">{e.event_type}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter text-white max-w-4xl leading-[0.98]">{e.title}</h1>
          <div className="flex flex-wrap gap-5 mt-4 text-white/80 text-sm">
            {dateLabel && <span className="inline-flex items-center gap-2"><Calendar className="h-4 w-4" />{dateLabel}</span>}
            {v && <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{v.name}</span>}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="event-hero-cta">
            <CTAButton
              productType="hotels"
              size="lg"
              label={v ? `Find hotels near ${v.name}` : "Find hotels"}
              testId="event-hero-hotels-cta"
              ctx={{ event: e.slug, eventDate: e.date, venue: v?.slug, city: v?.city_slug, page: `event_${e.slug}`, position: "hero" }}
            />
            <span className="text-xs text-white/70">Hotelstrips may earn a commission when you book.</span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
        <Breadcrumbs items={[{ label: "GameDay", to: "/gameday" }, { label: "Events" }, { label: e.title }]} />
        <p className="text-lg text-foreground/80 leading-relaxed max-w-3xl mb-6">{e.description}</p>
        <ShareBar title={e.title} path={`/events/${e.slug}`} className="mb-12" />
        <TicketAvailability slug={e.slug} />

        {v && <EventQuickPlan event={e} venue={v} stayArea={data.stay_areas?.[0]} pageName={`event_${e.slug}`} ctx={{ event: e.slug, eventDate: e.date }} />}
        {v && <VenueGuideBody bundle={data} ctx={{ event: e.slug, eventDate: e.date }} pageName={`event_${e.slug}`} />}
      </div>
    </>
  );
}
